/**
 * useMapFilters — the map's filter, search, sort and floor state, plus the
 * badge count that tells the user how much of it is switched on.
 * Extracted from useMainApp for maintainability.
 *
 * **Call this from `useMainApp`, never from `MapTab` (MUST).** The tabs are
 * conditionally rendered: leaving the map unmounts `MapTab` and everything it
 * owns. Today this state sits above them, so a climber can filter the map to
 * blue boulders, check the leaderboard and come back to the same filtered
 * view. Move the call down into the tab and that state resets on every switch —
 * silently, and only for the people who use the filters, which is exactly the
 * kind of regression nobody reports.
 *
 * The alternative would be persisting it (`useExpanderState` /
 * `usePersistedPeriod` are the pattern), but that is a different product
 * decision: it would also survive a reload, which a session-scoped filter
 * probably should not.
 *
 * `hasUngradedRoutes` lives here rather than with the route data because it is
 * a filter input: an ungraded route is its own bucket in the difficulty filter,
 * so it counts toward `totalDiffKeys` and therefore toward the badge.
 */
import { useState, useMemo, useCallback } from "react";
import { getMaps } from "../utils";
import { isMixedGym } from "../utils/disciplineUtils";
import { useTranslation } from "../i18n";

export function useMapFilters({ routes, diff, settings, gymMeta }) {
  const { t } = useTranslation();

  const [filters, setFilters] = useState({ difficulty: [], holdColor: [], status: "all", setters: [], compOnly: false });
  const [disciplineFilter, setDisciplineFilter] = useState("all"); // "all" | "boulder" | "lead"
  const [mapSearch, setMapSearch] = useState("");
  const [mapSort, setMapSort] = useState("diffAsc");
  const [showMapFilters, setShowMapFilters] = useState(false);
  const [activeMapId, setActiveMapId] = useState(null);

  const toggleDiff = useCallback(k => setFilters(f => ({ ...f, difficulty: f.difficulty.includes(k) ? f.difficulty.filter(d => d !== k) : [...f.difficulty, k] })), []);

  const maps = getMaps(settings, t);
  const currentMapId = activeMapId || maps[0]?.id || "default";
  const currentMap = maps.find(m => m.id === currentMapId) || maps[0];

  const hasUngradedRoutes = useMemo(() => routes.some(r => r.active && !r.difficulty), [routes]);
  const hasUnsetFieldRoutes = useMemo(() => routes.some(r => r.active && (r.difficulty == null || r.holdColor == null)), [routes]);
  const totalDiffKeys = Object.keys(diff).length + (hasUngradedRoutes ? 1 : 0);

  // Badge on the filter button. A difficulty filter that happens to select
  // every bucket counts as nothing switched on — otherwise "show everything"
  // would wear a badge saying six filters are active.
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (isMixedGym(gymMeta) && disciplineFilter !== "all") count++;
    if (filters.status !== "all") count++;
    if (filters.difficulty.length > 0 && filters.difficulty.length < totalDiffKeys) count += filters.difficulty.length;
    if (filters.compOnly) count++;
    return count;
  }, [filters, totalDiffKeys, disciplineFilter, gymMeta]);

  return {
    filters, setFilters, toggleDiff, activeFilterCount,
    disciplineFilter, setDisciplineFilter,
    mapSearch, setMapSearch, mapSort, setMapSort,
    showMapFilters, setShowMapFilters,
    activeMapId, setActiveMapId, maps, currentMapId, currentMap,
    hasUngradedRoutes, hasUnsetFieldRoutes,
  };
}
